import {
  releaseFocusTrap,
  unlockBodyScroll,
  unlockBackground,
} from '../../utils/FocusTrap';
import {
  railsuiModalContainer,
  railsuiModalController,
} from '../railsui_modal';

const DYNAMIC_VPE_ERROR_MODAL_ATTR = 'data-dynamic-vpe-error-modal';
const SAFE_CASE_EXPERIENCE_HTML_ID_PATTERN = /^[a-zA-Z][\w-]*$/;
const VPE_ERROR_MODAL_CONTROLLER = 'case-experience--vpe-error-modal';

function manualCloseVpeErrorModal(modalRoot) {
  const container = railsuiModalContainer(modalRoot);
  const content = modalRoot?.querySelector('[data-railsui-modal-target="content"]');
  if (!container || !content) {
    return false;
  }

  // Hide modal container and content
  [container, content].forEach((el) => el.classList.add('hidden'));

  // Remove overlay styles and set ARIA attributes
  container.classList.remove('bg-black/50', 'aquifer-modal-overlay');
  container.setAttribute('aria-hidden', 'true');

  // Release focus and unlock scroll/background
  releaseFocusTrap(container, { restoreFocus: false });
  unlockBackground(modalRoot);
  unlockBodyScroll();

  // Restore body state if needed
  if (document && document.body) {
    document.body.style.overflow = '';
    document.body.classList.remove('overflow-hidden');
  }

  return true;
}

export function isRailsuiModalOpen(modalRoot) {
  const container = railsuiModalContainer(modalRoot);
  return Boolean(container && !container.classList.contains('hidden'));
}

export function vpeErrorModalController(application, modalRoot) {
  if (!application || !modalRoot) return null;
  return application.getControllerForElementAndIdentifier(modalRoot, VPE_ERROR_MODAL_CONTROLLER);
}

function assertSafeCaseExperienceHtmlId(value, label) {
  if (typeof value !== 'string' || !SAFE_CASE_EXPERIENCE_HTML_ID_PATTERN.test(value)) {
    throw new Error(`Invalid ${label} for VPE error modal HTML`);
  }
}

function reportVpeErrorModalFailure(message, context = {}) {
  const payload = { message, ...context };
  if (typeof window !== 'undefined' && window.Rollbar?.warning) {
    window.Rollbar.warning(message, payload);
  } else if (typeof console !== 'undefined' && console.warn) {
    console.warn(message, payload);
  }
}

export function buildVpeErrorModalUrl(documentId, {
  variant = 'chat',
  modalId,
  targetController,
  targetName = 'errorModal',
  actionName = 'continueFromErrorModal',
  continueButtonId,
} = {}) {
  const params = new URLSearchParams({
    variant,
    modal_id: modalId,
    target_name: targetName,
    action_name: actionName,
  });
  if (targetController) params.set('target_controller', targetController);
  if (continueButtonId) params.set('continue_button_id', continueButtonId);
  return `/documents/${documentId}/case_experience/vpe_error_modal?${params}`;
}

export function closeRailsuiModal(application, modalRoot) {
  if (!modalRoot) return;

  const controller = vpeErrorModalController(application, modalRoot);
  if (controller) {
    controller.close();
    return;
  }

  const railsui = railsuiModalController(modalRoot, application);
  if (railsui?.isOpen) {
    railsui.close();
    return;
  }

  if (isRailsuiModalOpen(modalRoot)) {
    manualCloseVpeErrorModal(modalRoot);
  }
}

export function removeDynamicVpeErrorModals(application) {
  const stimulus = application || (typeof window !== 'undefined' ? window.Stimulus : null);
  document.querySelectorAll(`[${DYNAMIC_VPE_ERROR_MODAL_ATTR}]`).forEach((el) => {
    closeRailsuiModal(stimulus, el);
    el.remove();
  });
  document.body.style.overflow = '';
  document.body.classList.remove('overflow-hidden');
}

export async function waitForEmbeddedVpeErrorModalReady() {
  await Promise.resolve();
  await Promise.resolve();
  await Promise.resolve();
}

export function openVpeFallbackErrorModal(application, modalRoot) {
  const controller = vpeErrorModalController(application, modalRoot);
  if (!controller || !modalRoot?.isConnected) return false;
  return controller.open();
}

function buildMinimalVpeErrorModalHtml({
  modalId,
  continueButtonId,
  variant = 'chat',
}) {
  assertSafeCaseExperienceHtmlId(modalId, 'modalId');
  assertSafeCaseExperienceHtmlId(continueButtonId, 'continueButtonId');
  if (variant !== 'chat' && variant !== 'feedback') {
    throw new Error('Invalid variant for VPE error modal HTML');
  }

  return `
    <div
      id="${modalId}"
      class="vpe-fallback-error-modal-host"
      data-controller="railsui-modal ${VPE_ERROR_MODAL_CONTROLLER}"
      data-vpe-fallback-error-modal-variant="${variant}"
      data-case-experience--vpe-error-modal-variant-value="${variant}"
      data-case-experience--vpe-error-modal-dynamic-value="true"
      data-case-experience--vpe-error-modal-continue-button-id-value="${continueButtonId}"
      data-railsui-modal-click-outside-value="false"
      data-railsui-modal-initial-focus-selector-value="#${continueButtonId}"
      ${DYNAMIC_VPE_ERROR_MODAL_ATTR}="true"
    >
      <div
        data-railsui-modal-target="container"
        class="hidden fixed inset-0 z-50 overflow-y-auto"
        role="dialog"
        aria-hidden="true"
      >
        <div class="aquifer-modal-shell--wide">
          <div data-railsui-modal-target="content" class="hidden">
            <div class="aquifer-modal vpe-fallback-error-modal">
              <button
                type="button"
                class="aquifer-modal__close"
                aria-label="Continue without Virtual Patient Encounter"
                data-action="click->${VPE_ERROR_MODAL_CONTROLLER}#requestContinue"
              ></button>
              <h2 class="aquifer-modal__title aquifer-modal__title--wide">An Error has Occurred</h2>
              <div class="aquifer-modal__actions">
                <button
                  type="button"
                  class="btn btn-primary"
                  id="${continueButtonId}"
                  data-action="click->${VPE_ERROR_MODAL_CONTROLLER}#requestContinue"
                >Continue</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}

export async function mountVpeErrorModalFromHtml(html, { application } = {}) {
  removeDynamicVpeErrorModals(application);

  const wrapper = document.createElement('div');
  wrapper.innerHTML = html.trim();
  const modalRoot = wrapper.firstElementChild;
  if (!modalRoot) return null;

  modalRoot.setAttribute(DYNAMIC_VPE_ERROR_MODAL_ATTR, 'true');
  document.body.appendChild(modalRoot);

  const stimulus = application || (typeof window !== 'undefined' ? window.Stimulus : null);
  await waitForEmbeddedVpeErrorModalReady();
  openVpeFallbackErrorModal(stimulus, modalRoot);

  return modalRoot;
}

function mountMinimalVpeErrorModal({
  application,
  continueButtonId,
  modalId,
  variant = 'chat',
}) {
  const html = buildMinimalVpeErrorModalHtml({ modalId, continueButtonId, variant });
  return mountVpeErrorModalFromHtml(html, { application });
}

function tryOpenFallbackModalRoot(application, fallbackModalRoot) {
  return openVpeFallbackErrorModal(application, fallbackModalRoot);
}

export async function fetchAndMountVpeErrorModal({
  documentId,
  variant = 'chat',
  modalId,
  targetController,
  targetName = 'errorModal',
  actionName = 'continueFromErrorModal',
  continueButtonId,
  application,
  fallbackModalRoot,
} = {}) {
  if (!documentId || !modalId) {
    reportVpeErrorModalFailure('VPE error modal: missing required params', { documentId, modalId });
    return null;
  }

  const fetchUrl = buildVpeErrorModalUrl(documentId, {
    variant,
    modalId,
    targetController,
    targetName,
    actionName,
    continueButtonId,
  });

  let response;
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    response = await fetch(fetchUrl, {
      headers: { Accept: 'text/html' },
      credentials: 'same-origin',
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
  } catch (error) {
    reportVpeErrorModalFailure('VPE error modal: fetch failed', {
      documentId,
      modalId,
      error: String(error),
    });

    if (tryOpenFallbackModalRoot(application, fallbackModalRoot)) {
      return fallbackModalRoot;
    }

    return mountMinimalVpeErrorModal({
      application,
      continueButtonId: continueButtonId || (variant === 'feedback' ? 'vpe-feedback-error-continue-button' : 'vpe-chat-error-continue-button'),
      modalId: `${modalId}-fallback`,
      variant,
    });
  }

  if (!response.ok) {
    reportVpeErrorModalFailure('VPE error modal: fetch failed', {
      documentId,
      modalId,
      status: response.status,
    });

    if (tryOpenFallbackModalRoot(application, fallbackModalRoot)) {
      return fallbackModalRoot;
    }

    return mountMinimalVpeErrorModal({
      application,
      continueButtonId: continueButtonId || (variant === 'feedback' ? 'vpe-feedback-error-continue-button' : 'vpe-chat-error-continue-button'),
      modalId: `${modalId}-fallback`,
      variant,
    });
  }

  const html = await response.text();
  return mountVpeErrorModalFromHtml(html, { application });
}
